import React, { useMemo, useState } from 'react';
import { Col, Row, Slider, InputNumber } from 'antd';
import AppHeader from '../components/Header';

const numberFmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const currencyFmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const EarningsCalculator = () => {
  const [dailyViews, setDailyViews] = useState(200); // default 10k views/day
  const [cpm, setCpm] = useState(15); // default $5 CPM

  const metrics = useMemo(() => {
    const dailyEarnings = (dailyViews / 1000) * cpm;
    const monthlyViews = dailyViews * 30;
    const yearlyViews = dailyViews * 365;
    const monthlyEarnings = (monthlyViews / 1000) * cpm;
    const yearlyEarnings = (yearlyViews / 1000) * cpm;
    return {
      dailyEarnings,
      monthlyEarnings,
      yearlyEarnings,
      monthlyViews,
      yearlyViews,
    };
  }, [dailyViews, cpm]);

  const onDailyViewsChange = (val) => {
    if (typeof val === 'number' && !Number.isNaN(val)) setDailyViews(val);
  };

  const onCpmChange = (val) => {
    if (typeof val === 'number' && !Number.isNaN(val)) setCpm(val);
  };

  return (
    <div style={{ backgroundColor: '#FFF7F5', minHeight: '100vh' }}>
      <AppHeader />
      <div style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>
        <Col xs={23} sm={23} md={20} xl={16} xxl={10}>
          {/* top spacing under fixed header */}
          <br />
          <br />
          <br />
          <br />
          <br />

          <h1 style={{ fontWeight: 'bold', fontSize: '36px', marginBottom: 8, textAlign: 'center' }}>YouTube Earnings Calculator</h1>
          <p style={{ color: '#555', marginTop: 0, textAlign: 'center' }}>Estimate your ad revenue based on daily views and CPM.</p>

          <div style={{ border: 'solid lightgray 1px', borderRadius: '8px', backgroundColor: 'white', padding: '16px', marginTop: 16 }}>
            <div style={{ marginBottom: 24 }}>
              <span style={{ display: 'block', marginBottom: 8, fontSize: '18px', fontWeight: 600, color: '#3C3C3C' }}>Daily Views</span>
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={18}>
                  <Slider
                    min={0}
                    max={5000}
                    step={10}
                    value={dailyViews}
                    onChange={onDailyViewsChange}
                    tooltip={{ formatter: (v) => `${numberFmt.format(v)} views/day` }}
                  />
                </Col>
                <Col xs={24} md={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <InputNumber
                    min={0}
                    max={10000000}
                    step={1000}
                    value={dailyViews}
                    onChange={onDailyViewsChange}
                    style={{ width: '100%' }}
                    formatter={(v) => (v ? numberFmt.format(Number(String(v).replace(/,/g, ''))) : '0')}
                    parser={(v) => Number(String(v).replace(/,/g, ''))}
                  />
                </Col>
              </Row>
            </div>

            <div style={{ marginBottom: 8 }}>
              <span style={{ display: 'block', marginBottom: 8, fontSize: '18px', fontWeight: 600, color: '#3C3C3C' }}>CPM (Revenue per 1,000 views)</span>
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={18}>
                  <Slider
                    min={0}
                    max={50}
                    step={0.50}
                    value={cpm}
                    onChange={onCpmChange}
                    tooltip={{ formatter: (v) => currencyFmt.format(v) }}
                  />
                </Col>
                <Col xs={24} md={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <InputNumber
                    min={0}
                    max={1000}
                    step={0.25}
                    value={cpm}
                    onChange={onCpmChange}
                    style={{ width: '100%' }}
                    formatter={(v) => (v !== null && v !== undefined ? currencyFmt.format(Number(v)) : '$0')}
                    parser={(v) => Number(String(v).replace(/[^0-9.]/g, ''))}
                  />
                </Col>
              </Row>
            </div>
          </div>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <div style={{ border: 'solid lightgray 1px', borderRadius: '8px', backgroundColor: 'white', padding: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#3C3C3C' }}>Daily Earnings</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#3C3C3C' }}>{currencyFmt.format(metrics.dailyEarnings)}</div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ border: 'solid lightgray 1px', borderRadius: '8px', backgroundColor: 'white', padding: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#3C3C3C' }}>Monthly Earnings</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#3C3C3C' }}>{currencyFmt.format(metrics.monthlyEarnings)}</div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ border: 'solid lightgray 1px', borderRadius: '8px', backgroundColor: 'white', padding: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#3C3C3C' }}>Yearly Earnings</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#3C3C3C' }}>{currencyFmt.format(metrics.yearlyEarnings)}</div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ border: 'solid lightgray 1px', borderRadius: '8px', backgroundColor: 'white', padding: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#3C3C3C' }}>Views per Month</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#3C3C3C' }}>{numberFmt.format(metrics.monthlyViews)}</div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ border: 'solid lightgray 1px', borderRadius: '8px', backgroundColor: 'white', padding: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#3C3C3C' }}>Views per Year</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#3C3C3C' }}>{numberFmt.format(metrics.yearlyViews)}</div>
              </div>
            </Col>
          </Row>

          <br />
          <br />
          <br />
        </Col>
      </div>
    </div>
  );
};

export default EarningsCalculator;
